import { keyBy } from 'lodash'
import moment from 'moment'
import { labelFor, seedBucketKeys } from 'app/components/ai-agent-voice/newPatientCallsBuckets'
import type { BadgeColor } from 'app/components/ui/badge'
import type { GroupBy } from 'app/components/viz/DateGroupSelector'
import type { CallFailureType, ToolFlag, VoiceAgentOpsCounts, VoiceAgentOpsDailyCounts } from './voiceAgentOps.types'

export const TOOL_FLAGS: ToolFlag[] = [`toolError`, `toolTimeout`, `toolDuplicate`]

export const TOOL_FLAG_LABEL: Record<ToolFlag, string> = {
  toolError: `Error`,
  toolTimeout: `Timeout`,
  toolDuplicate: `Duplicate`,
}

export const TOOL_FLAG_COLOR: Record<ToolFlag, BadgeColor> = {
  toolError: `red`,
  toolTimeout: `amber`,
  toolDuplicate: `purple`,
}

export type ToolFlagCountKey = `toolErrorCalls` | `toolTimeoutCalls` | `toolDuplicateCalls`

export const TOOL_FLAG_COUNT_KEY: Record<ToolFlag, ToolFlagCountKey> = {
  toolError: `toolErrorCalls`,
  toolTimeout: `toolTimeoutCalls`,
  toolDuplicate: `toolDuplicateCalls`,
}

/** Card and column names for each flag's call count. */
export const TOOL_FLAG_METRIC_LABEL: Record<ToolFlag, string> = {
  toolError: `Tool Errors`,
  toolTimeout: `Tool Timeouts`,
  toolDuplicate: `Tool Duplicates`,
}

export const CALL_FAILURE_TYPES: CallFailureType[] = [
  `llmFailed`,
  `llmSlow`,
  `disconnected`,
  `maxDuration`,
  `error`,
  `unsuccessful`,
]

export type CallFailureGroup = `gpt` | `elevenLabs`

/** LLM failures file under GPT; everything else ElevenLabs reports files under ElevenLabs (plan D36). */
export const CALL_FAILURE_GROUP: Record<CallFailureType, CallFailureGroup> = {
  llmFailed: `gpt`,
  llmSlow: `gpt`,
  disconnected: `elevenLabs`,
  maxDuration: `elevenLabs`,
  error: `elevenLabs`,
  unsuccessful: `elevenLabs`,
}

export const CALL_FAILURE_GROUP_LABEL: Record<CallFailureGroup, string> = { gpt: `GPT`, elevenLabs: `ElevenLabs` }

export const CALL_FAILURE_GROUP_COLOR: Record<CallFailureGroup, BadgeColor> = { gpt: `blue`, elevenLabs: `orange` }

export const CALL_FAILURE_LABEL: Record<CallFailureType, string> = {
  llmFailed: `LLM Failed`,
  llmSlow: `LLM Slow`,
  disconnected: `Disconnected`,
  maxDuration: `Max Duration`,
  error: `Error`,
  unsuccessful: `Unsuccessful`,
}

export type CallFailureCountKey =
  | `llmFailedCalls`
  | `llmSlowCalls`
  | `disconnectedCalls`
  | `maxDurationCalls`
  | `errorCalls`
  | `unsuccessfulCalls`

export const CALL_FAILURE_COUNT_KEY: Record<CallFailureType, CallFailureCountKey> = {
  llmFailed: `llmFailedCalls`,
  llmSlow: `llmSlowCalls`,
  disconnected: `disconnectedCalls`,
  maxDuration: `maxDurationCalls`,
  error: `errorCalls`,
  unsuccessful: `unsuccessfulCalls`,
}

/** Shared by the KPI card info tooltips and the table header tooltips. */
export const METRIC_DEFINITIONS = {
  scheduleRate: `Appointments booked ÷ new patient calls. Same definition as the Dashboard tab.`,
  previousScheduleRate: `Schedule rate for the same number of days immediately before the selected range.`,
  scheduleRateChange: `Schedule rate minus the previous period's, in percentage points. Below zero means the schedule rate is dipping.`,
  aiCalls: `Every call the voice agent handled in the date range, excluding test calls.`,
  newPatientCalls: `Qualified calls tagged New Patient. Same definition as the Dashboard tab.`,
  previousNewPatientCalls: `New patient calls in the previous period.`,
  appointmentsBooked: `New patient calls tagged Appointment Scheduled, Rescheduled, or Confirmation.`,
  previousAppointmentsBooked: `Appointments booked in the previous period.`,
  appointmentsCompleted: `Appointments the voice agent booked that were later marked completed in the PMS.`,
  toolErrorCalls: `Calls ElevenLabs flagged tool_error: a tool the agent called returned an error.`,
  toolTimeoutCalls: `Calls ElevenLabs flagged tool_timeout: a tool the agent called did not respond in time.`,
  toolDuplicateCalls: `Calls ElevenLabs flagged tool_duplicate: the agent repeated the same tool call.`,
  failureRate: `Calls with any tool flag ÷ AI calls. A call with several flags counts once.`,
  gptFailedCalls: `Calls where the live-conversation LLM failed: ElevenLabs ended them with "custom_llm generation failed" or "Generating the LLM response took too long."`,
  elevenLabsFailedCalls: `Calls with an ElevenLabs failure: a dropped connection, the maximum duration reached, an error on the call, or ElevenLabs judging the call unsuccessful.`,
  llmFailedCalls: `ElevenLabs ended the call with "custom_llm generation failed".`,
  llmSlowCalls: `ElevenLabs ended the call with "Generating the LLM response took too long."`,
  disconnectedCalls: `The connection dropped: "Client disconnected: …" or "No user message received … network error".`,
  maxDurationCalls: `The call hit ElevenLabs' maximum conversation duration.`,
  errorCalls: `ElevenLabs attached an error code and reason to the call.`,
  unsuccessfulCalls: `ElevenLabs' post-call analysis marked the call unsuccessful.`,
  unbookedCalls: `New patient calls with no Appointment Scheduled, Rescheduled, or Confirmation tag.`,
}

export const EMPTY_COUNTS: VoiceAgentOpsCounts = {
  aiCalls: 0,
  newPatientCalls: 0,
  appointmentsBooked: 0,
  appointmentsCompleted: 0,
  toolErrorCalls: 0,
  toolTimeoutCalls: 0,
  toolDuplicateCalls: 0,
  failedCalls: 0,
  llmFailedCalls: 0,
  llmSlowCalls: 0,
  disconnectedCalls: 0,
  maxDurationCalls: 0,
  errorCalls: 0,
  unsuccessfulCalls: 0,
  gptFailedCalls: 0,
  elevenLabsFailedCalls: 0,
  previousNewPatientCalls: 0,
  previousAppointmentsBooked: 0,
}

const COUNT_KEYS = Object.keys(EMPTY_COUNTS) as (keyof VoiceAgentOpsCounts)[]

export type VoiceAgentOpsRates = {
  scheduleRate?: number
  failureRate?: number
  previousScheduleRate?: number
  /** Schedule rate minus the previous period's, in points; undefined unless both periods had new patient calls. */
  scheduleRateChange?: number
}

/** Percent rates from counts; undefined (rendered `-`) when the denominator is zero. */
export function ratesFor(counts: VoiceAgentOpsCounts): VoiceAgentOpsRates {
  const scheduleRate = counts.newPatientCalls ? (counts.appointmentsBooked / counts.newPatientCalls) * 100 : undefined
  const previousScheduleRate = counts.previousNewPatientCalls
    ? (counts.previousAppointmentsBooked / counts.previousNewPatientCalls) * 100
    : undefined
  return {
    scheduleRate,
    failureRate: counts.aiCalls ? (counts.failedCalls / counts.aiCalls) * 100 : undefined,
    previousScheduleRate,
    scheduleRateChange:
      scheduleRate === undefined || previousScheduleRate === undefined
        ? undefined
        : scheduleRate - previousScheduleRate,
  }
}

/** Sums counts across rows and derives weighted rates (total ÷ total), never an average of row rates. */
export function totalCounts(rows: VoiceAgentOpsCounts[]): VoiceAgentOpsCounts & VoiceAgentOpsRates {
  const totals = { ...EMPTY_COUNTS }
  for (const row of rows) {
    for (const key of COUNT_KEYS) totals[key] += row[key]
  }
  return { ...totals, ...ratesFor(totals) }
}

/** Locations whose schedule rate fell versus the previous period (any drop, plan D37), biggest drop first. */
export function dippingLocations<T extends VoiceAgentOpsCounts>(rows: T[]): T[] {
  return rows
    .map((row) => ({ row, change: ratesFor(row).scheduleRateChange }))
    .filter((entry): entry is { row: T; change: number } => entry.change !== undefined && entry.change < 0)
    .sort((a, b) => a.change - b.change)
    .map(({ row }) => row)
}

export type DailyCountField = Exclude<keyof VoiceAgentOpsDailyCounts, `date`>

const EMPTY_DAILY_TOTALS: Record<DailyCountField, number> = {
  aiCalls: 0,
  failedCalls: 0,
  gptFailedCalls: 0,
  elevenLabsFailedCalls: 0,
}

/** One day's counts, keyed by field. Voice's `VoiceAgentOpsDailyCounts` is one; the SMS Ops tab has its own. */
export type DailyCounts<F extends string> = { date: string } & Record<F, number>

export type CountsBucket<F extends string = DailyCountField> = {
  label: string
  tooltip: string
  totals: Record<F, number>
}

/**
 * Blocks inference from the rows (TypeScript 5.2 has no built-in `NoInfer`): the fields come
 * from `emptyTotals` or the default, never from a row, whose `date` is not a count.
 */
type NoInferFields<T> = [T][T extends unknown ? 0 : never]

/**
 * Rolls daily counts up into Days/Weeks/Months buckets, seeding empty buckets across the
 * range. `emptyTotals` names the fields to sum; it defaults to the voice agent's.
 */
export function bucketDailyCounts<F extends string = DailyCountField>(
  daily: DailyCounts<NoInferFields<F>>[],
  fromDate: string,
  toDate: string,
  groupBy: GroupBy,
  emptyTotals: Record<F, number> = EMPTY_DAILY_TOTALS as Record<F, number>,
): CountsBucket<F>[] {
  const buckets = new Map(seedBucketKeys(fromDate, toDate, groupBy).map((key) => [key, { ...emptyTotals }]))
  for (const day of daily) {
    const totals = buckets.get(moment.utc(day.date).startOf(groupBy).format(`YYYY-MM-DD`))
    if (!totals) continue
    for (const field of Object.keys(emptyTotals) as F[]) totals[field] += day[field]
  }
  return [...buckets.entries()].map(([key, totals]) => ({ ...labelFor(key, groupBy), totals }))
}

/** ElevenLabs `no_booking_reason` categories in the agent's priority order (plan D42). */
export const NO_BOOKING_REASONS: { token: string; label: string }[] = [
  { token: `reschedule_or_cancel`, label: `Reschedule or Cancel` },
  { token: `not_booking_intent`, label: `Not Trying to Book` },
  { token: `service_not_offered`, label: `Service Not Offered` },
  { token: `out_of_range`, label: `Out of Range` },
  { token: `insurance_state_hmo`, label: `Insurance: State / HMO` },
  { token: `insurance_out_of_network`, label: `Insurance: Out of Network` },
  { token: `cost`, label: `Cost` },
  { token: `no_suitable_availability`, label: `No Suitable Availability` },
  { token: `requested_callback`, label: `Requested Callback` },
  { token: `caller_will_call_back`, label: `Caller Will Call Back` },
  { token: `transferred_to_human`, label: `Transferred to Human` },
  { token: `agent_failure`, label: `Agent Failure` },
  { token: `caller_disconnected`, label: `Caller Disconnected` },
  { token: `language_barrier`, label: `Language Barrier` },
  { token: `other`, label: `Other` },
]

/** Breakdown key for unbooked calls with no reason. */
export const NOT_REPORTED_REASON = `not_reported`

const NO_BOOKING_REASON_LABEL: Record<string, string> = Object.fromEntries(
  NO_BOOKING_REASONS.map(({ token, label }) => [token, label]),
)

/** Label for a reason token. An unrecognized token is shown as-is, so a prompt change can't hide data. */
export function noBookingReasonLabel(token?: string | null): string {
  if (!token || token === NOT_REPORTED_REASON) return `Not Reported`
  return NO_BOOKING_REASON_LABEL[token] ?? token
}

export type ReasonBreakdownRow = { reason: string; label: string; count: number; share: number }

/** Unbooked calls grouped by reason (none → Not reported), largest first; share is a percent of all of them. */
export function reasonBreakdown(calls: { noBookingReason?: string | null }[]): ReasonBreakdownRow[] {
  const counts = new Map<string, number>()
  for (const call of calls) {
    const reason = call.noBookingReason || NOT_REPORTED_REASON
    counts.set(reason, (counts.get(reason) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([reason, count]) => ({
      reason,
      label: noBookingReasonLabel(reason),
      count,
      share: (count / calls.length) * 100,
    }))
    .sort((a, b) => b.count - a.count)
}

/** A location is slipping when it has gone this long without an AI call. */
export const SLIPPING_THRESHOLD_HOURS = 48
export const SLIPPING_THRESHOLD_LABEL = `48h`

/** One location's AI calls per day. */
export type LocationDailyCalls = {
  locationId: string
  /** Last day with an AI call before the range starts; undefined when there is none. */
  lastCallBeforeRange?: string
  /** YYYY-MM-DD → AI calls that day. Days without calls are omitted. */
  callsByDate: Record<string, number>
}

export type SlippingDay = {
  date: string
  calls: number
  /** Whole days since the last call as of this day; undefined when there is no call on record. */
  daysSinceCall?: number
  /** Length of the no-call gap this day sits in (so far, if still running). */
  gapDays?: number
}

export type SlippingRow = {
  locationId: string
  locationName: string
  days: SlippingDay[]
  /** No AI call for at least the threshold as of the last day shown, so that day is always a no-call day. */
  isSlipping: boolean
  /** Days without a call as of the last day shown (0 if it had calls), for ordering. */
  endDaysSinceCall: number
}

/**
 * Each day from the start of the range through today (never past it) with its call
 * count and, on a day without calls, how long the location had gone quiet. Daily
 * resolution: any call resets the count.
 */
export function slippingDays(
  daily: LocationDailyCalls | undefined,
  fromDate: string,
  toDate: string,
  today: moment.Moment,
): SlippingDay[] {
  let lastCallDay = daily?.lastCallBeforeRange ? moment.utc(daily.lastCallBeforeRange).startOf(`day`) : undefined
  // `today` is a moment in the caller's zone; take its calendar day, and compare in the
  // same plain-date space as the range strings rather than converting it to UTC.
  const end = moment.min(moment.utc(toDate), moment.utc(today.format(`YYYY-MM-DD`)))
  const days: SlippingDay[] = []
  for (let day = moment.utc(fromDate); day.isSameOrBefore(end, `day`); day.add(1, `day`)) {
    const date = day.format(`YYYY-MM-DD`)
    const calls = daily?.callsByDate[date] ?? 0
    if (calls > 0) lastCallDay = day.clone()
    days.push({ date, calls, daysSinceCall: lastCallDay ? day.diff(lastCallDay, `days`) : undefined })
  }

  // Walk back so every no-call day learns the length of the gap it sits in (for the
  // tooltip): the gap's last day knows how long it had been since the call.
  let gapDays: number | undefined
  let inGap = false
  for (let i = days.length - 1; i >= 0; i--) {
    const day = days[i]
    if (day.calls > 0) {
      inGap = false
      continue
    }
    if (!inGap) {
      gapDays = day.daysSinceCall
      inGap = true
    }
    day.gapDays = gapDays
  }
  return days
}

/**
 * Grid rows, longest current gap first. A location is slipping when, as of the last day
 * shown, it has gone the threshold (rounded up to whole days, 48h = 2 days) or longer
 * without a call, or has no call on record.
 */
export function slippingRows(
  locations: { id?: string; name?: string | null }[],
  daily: LocationDailyCalls[],
  fromDate: string,
  toDate: string,
  thresholdHours: number,
  today: moment.Moment,
): SlippingRow[] {
  const thresholdDays = Math.max(1, Math.ceil(thresholdHours / 24))
  const dailyById = keyBy(daily, `locationId`)
  return locations
    .map((location) => {
      const days = slippingDays(dailyById[location.id!], fromDate, toDate, today)
      const lastDay = days[days.length - 1]
      const endDaysSinceCall = !lastDay || lastDay.calls > 0 ? 0 : (lastDay.daysSinceCall ?? Number.MAX_SAFE_INTEGER)
      return {
        locationId: location.id!,
        locationName: location.name ?? `Unnamed location`,
        days,
        isSlipping: !!lastDay && endDaysSinceCall >= thresholdDays,
        endDaysSinceCall,
      }
    })
    .sort((a, b) => b.endDaysSinceCall - a.endDaysSinceCall)
}
